import {
    Node,
    NodeType,
    InputType,
    SequenceType,
    SurveySession,
    OptionGroup,
    SomatConfig,
    SessionMode,
    IndicatorResponse
} from "@/types/models";
import {createContext, Dispatch, PropsWithChildren, useContext, useState} from "react";
import {SurveySessionInertiaProps} from "@/app";
import {PageProps} from "@/types";
import {flattenHierarchy} from "@/services/NodeService";
import {readSessionLayout, storeSessionLayout} from "@/utils/sessionLayout";

type SessionMetadataContextProps = {
    init: (props: SurveySessionInertiaProps) => void
    session: SurveySession | undefined
    setSession: Dispatch<SurveySession | undefined>
    survey: SurveySession['survey'] | undefined
    setSurvey: Dispatch<SurveySession['survey'] | undefined>
    rootNode: Node | undefined
    setRootNode: Dispatch<Node | undefined>
    initialNode: Node | undefined
    setInitialNode: Dispatch<Node | undefined>
    nodeTypes: NodeType[] | undefined
    setNodeTypes: Dispatch<NodeType[] | undefined>
    inputTypes: InputType[] | undefined
    setInputTypes: Dispatch<InputType[] | undefined>
    optionGroups: OptionGroup[] | undefined
    setOptionGroups: Dispatch<OptionGroup[] | undefined>
    sequenceTypes: SequenceType[] | undefined
    setSequenceTypes: Dispatch<SequenceType[] | undefined>
    responses: IndicatorResponse[] | undefined
    setResponses: Dispatch<IndicatorResponse[] | undefined>
    mode: SessionMode
    setMode: Dispatch<SessionMode>
    panelSizes: Record<string, number>
    setPanelSizes: Dispatch<Record<string, number>>
    config: SomatConfig
}

const SessionMetadataContext = createContext<SessionMetadataContextProps | undefined>(undefined)

export const SessionMetadataContextProvider = (
    {children, config}: PropsWithChildren<SurveySessionInertiaProps & PageProps>
) => {
    const [session, setSession] = useState<SurveySession | undefined>(undefined)
    const [survey, setSurvey] = useState<SurveySession['survey'] | undefined>(undefined)
    const [rootNode, setRootNode] = useState<Node | undefined>(undefined)
    const [initialNode, setInitialNode] = useState<Node | undefined>(undefined)
    const [nodeTypes, setNodeTypes] = useState<NodeType[] | undefined>(undefined)
    const [inputTypes, setInputTypes] = useState<InputType[] | undefined>(undefined)
    const [optionGroups, setOptionGroups] = useState<OptionGroup[] | undefined>(undefined)
    const [sequenceTypes, setSequenceTypes] = useState<SequenceType[] | undefined>(undefined)
    const [responses, setResponses] = useState<IndicatorResponse[] | undefined>(undefined)
    const [mode, setMode] = useState<SessionMode>('respond')
    const [panelSizes, setPanelSizesState] = useState<Record<string, number>>(
        () => readSessionLayout('panelSizes') ?? {}
    )

    /**
     * Kept for the next visit on this device, like the choice to pin the contents.
     */
    const setPanelSizes = (sizes: Record<string, number>) => {
        setPanelSizesState(sizes)
        storeSessionLayout('panelSizes', sizes)
    }

    const init = (props: SurveySessionInertiaProps) => {
        const rootNode = props.session?.survey?.rootNode
        setRootNode(rootNode)
        setSurvey(props.session?.survey)
        setSession(props.session)
        setInitialNode(props.initialNode)
        setMode(props.mode ?? (props.session ? 'respond' : 'index'))
        setNodeTypes(props.nodeTypes)
        setInputTypes(props.inputTypes)
        setOptionGroups(props.optionGroups)
        setSequenceTypes(props.sequenceTypes)
    }

    return <SessionMetadataContext.Provider value={{
        init,
        session,
        setSession,
        survey,
        setSurvey,
        rootNode,
        setRootNode,
        initialNode,
        setInitialNode,
        nodeTypes,
        setNodeTypes,
        inputTypes,
        setInputTypes,
        optionGroups,
        setOptionGroups,
        sequenceTypes,
        setSequenceTypes,
        responses,
        setResponses,
        mode,
        setMode,
        panelSizes,
        setPanelSizes,
        config,
    }}>
        {children}
    </SessionMetadataContext.Provider>
}

export function useSessionMetadataContext(): SessionMetadataContextProps {
    const context = useContext(SessionMetadataContext);
    if (!context) throw new Error("No SessionMetadataContext is defined")
    return context
}
